import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { shiftAssignmentTargetSchema, shiftMemberDutySchema, shiftStatusSchema, shiftTotalizerSourceSchema, staffDutySchema, } from "./enums.js";
/**
 * Vardiya açılışı, üyeler, atamalar ve vardiya içi devir (Prompt 13 — API A aşaması).
 *
 * Değişmezler:
 *  · İŞ GÜNÜ vardiyadan gelir: şablon varsa `shiftWindow`, yoksa vardiyanın başladığı İstanbul takvim günü.
 *  · Bir istasyonda aynı anda TEK açık vardiya vardır (`SHIFT_ALREADY_OPEN`).
 *  · Aynı tabanca aynı anda TEK kişidedir (`NOZZLE_ALREADY_ASSIGNED`; veritabanında dışlama kısıtı).
 *  · Açılış endeksi akaryakıt modülünün SON okumasından gelir; elle değer gerekçe ister ve akaryakıt çekirdeğine
 *    `MANUAL` okuma olarak da yazılır (mutabakat görsün).
 *  · Kapanış, beklenen tutar, Z raporu ve gün sonu B / C aşamalarındadır; bu dosya onların şemalarını TAŞIMAZ.
 */
const reasonSchema = z.string().trim().min(3).max(500);
const noteSchema = z.string().trim().min(1).max(500);
/** Açılış / devir endeksi girdisi. `reason` yalnızca son okumadan sapma toleransı aşılırsa zorunludur. */
export const shiftTotalizerInputSchema = z.object({
    nozzleId: uuidSchema,
    value: quantitySchema.describe("Elle okunan elektronik endeks (L)"),
    reason: reasonSchema.optional().describe("Son okumadan sapma toleransı aşılırsa zorunlu"),
});
/** Açılışta alınan tank okuması — akaryakıt alım çekirdeğine `MANUAL` okuma olarak yazılır. */
export const shiftTankReadingInputSchema = z.object({
    tankId: uuidSchema,
    levelMm: quantitySchema,
    waterMm: quantitySchema.nullable().optional(),
    temperatureC: z
        .string()
        .regex(/^-?\d{1,3}(\.\d{1,2})?$/, "Sıcaklık sayı dizgesi olmalıdır")
        .nullable()
        .optional(),
});
/** Vardiyaya alınan kişi: kimin hangi tabancalarda ve hangi kasada çalıştığı. */
export const shiftMemberInputSchema = z.object({
    staffId: uuidSchema,
    duty: shiftMemberDutySchema,
    openingFloat: nonNegativeMoneySchema.optional().describe("Devir parası (kuruş); yoksa ayardaki varsayılan"),
    cashRegisterId: uuidSchema
        .nullable()
        .optional()
        .describe("Kişinin nakdinin gireceği kasa; yoksa vardiyanın kasası"),
    nozzleIds: z.array(uuidSchema).max(200).optional(),
    dispenserIds: z.array(uuidSchema).max(100).optional().describe("Pompanın TÜM etkin tabancaları atanır"),
    islandNos: z
        .array(z.number().int().min(1).max(99))
        .max(50)
        .optional()
        .describe("Adadaki pompaların tüm etkin tabancaları atanır"),
    assignedCashRegisterId: uuidSchema
        .nullable()
        .optional()
        .describe("Kasiyerin sorumlu olduğu kasa (CASH_REGISTER ataması)"),
});
export const openShiftSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema,
    templateId: uuidSchema.nullable().optional(),
    name: z.string().trim().min(1).max(60).optional().describe("Yoksa şablon adı ya da açılış saati"),
    startDate: localDateSchema
        .optional()
        .describe("Şablonlu vardiyanın BAŞLADIĞI takvim günü; yoksa `openedAt`'in İstanbul günü"),
    openedAt: timestampSchema.optional().describe("Yoksa şimdi. Geçmiş kabul edilir, gelecek reddedilir"),
    cashRegisterId: uuidSchema.nullable().optional().describe("Vardiyanın varsayılan kasası"),
    previousUnclosedReason: reasonSchema
        .optional()
        .describe("Önceki vardiya kapanmamışken açılış gerekçesi (ayar açıksa)"),
    note: z.string().trim().min(1).max(1000).optional(),
    members: z.array(shiftMemberInputSchema).min(1).max(50),
    totalizers: z.array(shiftTotalizerInputSchema).max(200).optional(),
    tankReadings: z.array(shiftTankReadingInputSchema).max(50).optional(),
});
// ── Yanıt ────────────────────────────────────────────────────────────────────
export const shiftAssignmentSchema = z.object({
    id: uuidSchema,
    memberId: uuidSchema,
    staffId: uuidSchema,
    /** KVKK: kişisel veri — yalnızca bu yanıtta döner. */
    staffName: z.string(),
    targetType: shiftAssignmentTargetSchema,
    nozzleId: uuidSchema.nullable(),
    nozzleLabel: z.string().nullable().describe("Ada / pompa / tabanca etiketi (ör. 1. ada · P2 · T3)"),
    dispenserId: uuidSchema.nullable(),
    islandNo: z.number().int().nullable(),
    productId: uuidSchema.nullable(),
    cashRegisterId: uuidSchema.nullable(),
    cashRegisterName: z.string().nullable(),
    fromAt: timestampSchema,
    toAt: timestampSchema.nullable(),
    openingTotalizer: quantitySchema.nullable(),
    openingSource: shiftTotalizerSourceSchema.nullable(),
    openingOverrideReason: z.string().nullable(),
    closingTotalizer: quantitySchema.nullable(),
    closingSource: shiftTotalizerSourceSchema.nullable(),
    /** Aralıktaki endeks artışı (taşma ve onaylı düzeltmeler çözülmüş); kapanış başlatılınca DONDURULUR. */
    totalizerLitres: quantitySchema.nullable(),
    handoverNote: z.string().nullable(),
});
export const shiftMemberSchema = z.object({
    id: uuidSchema,
    staffId: uuidSchema,
    staffCode: z.string(),
    /** KVKK: kişisel veri — yalnızca bu yanıtta döner. */
    staffName: z.string(),
    duty: shiftMemberDutySchema,
    staffDuty: staffDutySchema,
    userId: uuidSchema.nullable(),
    counterpartyId: uuidSchema.nullable(),
    cashRegisterId: uuidSchema.nullable(),
    cashRegisterName: z.string().nullable(),
    openingFloat: moneySchema,
    floatTransactionId: uuidSchema.nullable(),
    joinedAt: timestampSchema,
    leftAt: timestampSchema.nullable(),
    /** Vardiya içi kayıt toplamları (iptal edilmişler hariç). */
    cashDropTotal: moneySchema,
    cashExpenseTotal: moneySchema,
    creditSaleTotal: moneySchema,
    testDrawLitres: quantitySchema,
});
const shiftCore = {
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    templateId: uuidSchema.nullable(),
    shiftNo: z.string(),
    name: z.string(),
    businessDate: localDateSchema,
    plannedStartAt: timestampSchema.nullable(),
    plannedEndAt: timestampSchema.nullable(),
    status: shiftStatusSchema,
    cashRegisterId: uuidSchema.nullable(),
    openedAt: timestampSchema,
    openedBy: uuidSchema.nullable(),
    previousUnclosedReason: z.string().nullable(),
    closingStartedAt: timestampSchema.nullable(),
    closedAt: timestampSchema.nullable(),
    approvedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    note: z.string().nullable(),
    /** İş günü kilitliyse vardiyaya artık yazılamaz (`BUSINESS_DAY_LOCKED`). */
    businessDayLocked: z.boolean(),
    createdAt: timestampSchema,
    version: z.number().int().min(1),
};
export const shiftSummarySchema = z.object({
    ...shiftCore,
    memberCount: z.number().int().nonnegative(),
    nozzleCount: z.number().int().nonnegative(),
});
export const shiftTotalsSchema = z.object({
    openingFloat: moneySchema,
    cashDropTotal: moneySchema,
    cashExpenseTotal: moneySchema,
    creditSaleTotal: moneySchema,
    testDrawLitres: quantitySchema,
    cashDropCount: z.number().int().nonnegative(),
    cashExpenseCount: z.number().int().nonnegative(),
    creditSaleCount: z.number().int().nonnegative(),
    testDrawCount: z.number().int().nonnegative(),
});
export const shiftSchema = z.object({
    ...shiftCore,
    members: z.array(shiftMemberSchema),
    assignments: z.array(shiftAssignmentSchema),
    totals: shiftTotalsSchema,
});
export const listShiftsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    status: shiftStatusSchema.optional(),
    from: localDateSchema.optional().describe("İş günü (dâhil)"),
    to: localDateSchema.optional().describe("İş günü (dâhil)"),
});
export const shiftPageSchema = pageOf(shiftSummarySchema);
// ── Pano ─────────────────────────────────────────────────────────────────────
export const shiftBoardQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
});
export const shiftBoardMemberSchema = z.object({
    memberId: uuidSchema,
    staffId: uuidSchema,
    staffCode: z.string(),
    /** KVKK: kişisel veri — yalnızca bu yanıtta döner. */
    staffName: z.string(),
    duty: shiftMemberDutySchema,
    cashRegisterId: uuidSchema.nullable(),
    cashRegisterName: z.string().nullable(),
    islandNos: z.array(z.number().int()),
    dispenserNos: z.array(z.number().int()),
    nozzleLabels: z.array(z.string()),
    openingFloat: moneySchema,
    cashDropTotal: moneySchema,
    /**
     * İZLENEN nakit = devir parası + ara teslimler. Canlı satış TAHMİNİ DEĞİLDİR (o, kapanıştaki beklenen
     * tutarın işidir — B aşaması).
     */
    trackedCash: moneySchema,
});
export const shiftBoardEntrySchema = z.object({
    shiftId: uuidSchema,
    shiftNo: z.string(),
    name: z.string(),
    stationId: uuidSchema,
    stationName: z.string(),
    businessDate: localDateSchema,
    status: shiftStatusSchema,
    openedAt: timestampSchema,
    plannedEndAt: timestampSchema.nullable(),
    members: z.array(shiftBoardMemberSchema),
});
export const shiftBoardSchema = z.object({ items: z.array(shiftBoardEntrySchema) });
// ── Üye / atama / devir / iptal ──────────────────────────────────────────────
export const addShiftMemberSchema = shiftMemberInputSchema.extend({
    joinedAt: timestampSchema.optional().describe("Yoksa şimdi (vardiya açılışından önce olamaz)"),
    totalizers: z.array(shiftTotalizerInputSchema).max(200).optional(),
});
export const addShiftAssignmentSchema = z.object({
    memberId: uuidSchema,
    nozzleIds: z.array(uuidSchema).max(200).optional(),
    dispenserIds: z.array(uuidSchema).max(100).optional(),
    islandNos: z.array(z.number().int().min(1).max(99)).max(50).optional(),
    cashRegisterId: uuidSchema.optional().describe("Kasa ataması (CASH_REGISTER)"),
    at: timestampSchema.optional().describe("Yoksa şimdi"),
    totalizers: z.array(shiftTotalizerInputSchema).max(200).optional(),
});
/**
 * Vardiya içi devir (mola / vardiya amiri değişimi): eski atamalar kapanır, yenisi AYNI endeksle açılır.
 * `toMemberId` verilmezse `toStaff` ile yeni üye eklenir.
 */
export const shiftHandoverSchema = z
    .object({
    nozzleIds: z.array(uuidSchema).min(1).max(200),
    toMemberId: uuidSchema.optional(),
    toStaff: shiftMemberInputSchema
        .pick({
        staffId: true,
        duty: true,
        openingFloat: true,
        cashRegisterId: true,
    })
        .optional(),
    at: timestampSchema.optional().describe("Yoksa şimdi"),
    totalizers: z.array(shiftTotalizerInputSchema).max(200).optional(),
    note: noteSchema.optional(),
})
    .refine((value) => (value.toMemberId === undefined) !== (value.toStaff === undefined), {
    path: ["toMemberId"],
    message: "Devir için ya mevcut bir üye (toMemberId) ya da yeni personel (toStaff) verilmelidir",
});
export const cancelShiftSchema = z.object({ reason: reasonSchema });
export const shiftParamsSchema = z.object({ id: uuidSchema });
//# sourceMappingURL=shifts.js.map